import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import styled from "@emotion/styled";
import Collapsible from "react-collapsible";

import AreaTypes from "types/AreaTypes";
import PointTypes from "types/PointTypes";
import PathTypes from "types/PathTypes";

import ParkkiTheme from "themes/ParkkiTheme";

import UnselectedChipsSection from "components/UnselectedChipsSection";
import CollapsibleHeader from "components/CollapsibleHeader";

const Container = styled.div`
  background-color: #f5f2f2;
`;

const SelectAllTitle = styled.div`
  cursor: pointer;
  color: ${ParkkiTheme.colors.turquoise};
  margin-top: ${ParkkiTheme.spacings.xsmall};

  &:hover {
    color: ${ParkkiTheme.colors.turquoise3};
  }
`;

const UncollapsedContainer = styled.div`
  padding-bottom: ${ParkkiTheme.spacings.xsmall};
  padding-right: ${ParkkiTheme.spacings.base};
  padding-left: ${ParkkiTheme.spacings.base};
`;

type Props = {
  allSelected: boolean;
  nbSelected: number;
  areas: AreaTypes[];
  points: PointTypes[];
  paths: PathTypes[];
  onSelectLayers: Function;
};

function SelectLayersCollapsibleSection({
  allSelected,
  nbSelected,
  areas,
  points,
  paths,
  onSelectLayers,
}: Props): JSX.Element {
  const { formatMessage } = useIntl();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (nbSelected === 0 || allSelected) {
      setCollapsed(true);
    }
  }, [allSelected, nbSelected]);

  function handleCollapsibleAction() {
    setCollapsed(!collapsed);
  }

  function handleSelectAllLayers() {
    onSelectLayers("selectAll", [...areas, ...points, ...paths]);
  }

  function handleUnselectAllLayers() {
    onSelectLayers("unselectAll", []);
  }

  return (
    <Container>
      <Collapsible
        transitionTime={allSelected ? 10 : 200}
        open={collapsed}
        onTriggerOpening={handleCollapsibleAction}
        onTriggerClosing={handleCollapsibleAction}
        trigger={
          <CollapsibleHeader allSelected={allSelected} collapsed={collapsed} />
        }
      >
        <UncollapsedContainer>
          <UnselectedChipsSection
            label={formatMessage({ id: "components.selectALayerInfo.areas" })}
            layers={areas}
            onSelectLayers={(layers: AreaTypes[]) =>
              onSelectLayers("area", layers)
            }
          />

          <UnselectedChipsSection
            label={formatMessage({ id: "components.selectALayerInfo.points" })}
            layers={points}
            onSelectLayers={(layers: PointTypes[]) =>
              onSelectLayers("point", layers)
            }
          />

          <UnselectedChipsSection
            label={formatMessage({ id: "components.selectALayerInfo.paths" })}
            layers={paths.filter((path) => path.apiType !== "TRAFFIC")}
            onSelectLayers={(layers: PathTypes[]) => {
              onSelectLayers("pathNonTraffic", layers);
            }}
          />

          <UnselectedChipsSection
            label={formatMessage({
              id: "components.selectALayerInfo.pathsTraffic",
            })}
            layers={paths.filter((path) => path.apiType === "TRAFFIC")}
            onSelectLayers={(layers: PathTypes[]) =>
              onSelectLayers("pathTraffic", layers)
            }
          />

          {allSelected ? (
            <SelectAllTitle onClick={handleUnselectAllLayers}>
              {formatMessage({
                id: `components.selectedLayersSection.unselectAllLayers.${
                  areas?.length + points?.length + paths?.length === 1
                    ? "one"
                    : "other"
                }`,
              })?.toUpperCase()}
            </SelectAllTitle>
          ) : (
            <SelectAllTitle onClick={handleSelectAllLayers}>
              {formatMessage({
                id: `components.selectedLayersSection.selectAllLayers.${
                  areas?.length + points?.length + paths?.length === 1
                    ? "one"
                    : "other"
                }`,
              })?.toUpperCase()}
            </SelectAllTitle>
          )}
        </UncollapsedContainer>
      </Collapsible>
    </Container>
  );
}

SelectLayersCollapsibleSection.defaultProps = {
  areas: [],
  points: [],
  paths: [],
};

export default SelectLayersCollapsibleSection;
